import { newChat, mergeMessages, requestId } from './model'
import { createChatPreferences } from './preferences'

// Network state lives per chat; late responses never replace the selected chat.
export function createChatController(api, set) {
  const state = { chats: {}, sessions: [], selected: null, loading: false, error: '', nextPage: null, sort: 'activity', sortBusy: false }
  let listGeneration = 0
  function remember(row) {
    if (!state.chats[row.id]) set(state.chats, row.id, { ...newChat(row.id), saved: true })
    state.chats[row.id].title = row.title
    state.chats[row.id].isPinned = Boolean(row.is_pinned)
    return state.chats[row.id]
  }
  function pinnedFirst(rows) {
    return [...rows.filter(row => state.chats[row.id].isPinned), ...rows.filter(row => !state.chats[row.id].isPinned)]
  }
  function start() {
    const chat = newChat()
    set(state.chats, chat.id, chat)
    state.sessions = pinnedFirst([{ id: chat.id, title: chat.title }, ...state.sessions])
    state.selected = chat.id
    return chat
  }
  async function list(more = false) {
    if (more && state.loading) return
    const generation = ++listGeneration
    state.loading = true
    state.error = ''
    try {
      const page = more ? state.nextPage : 1
      if (!page) return
      const { data } = await api.list({ page })
      if (generation !== listGeneration) return
      data.results.forEach(remember)
      const fresh = new Map(data.results.map(row => [row.id, row]))
      if (more) {
        state.sessions = [...state.sessions.filter(row => !fresh.has(row.id)), ...data.results]
      } else {
        state.sessions = [...state.sessions.filter(row => !state.chats[row.id].saved), ...data.results]
      }
      state.sessions = pinnedFirst(state.sessions)
      if (more || page === 1) state.nextPage = data.next ? page + 1 : null
      if (!state.selected) state.selected = state.sessions[0]?.id || start().id
    } catch (_) {
      if (generation === listGeneration) state.error = 'Unable to load chats. Try again.'
    } finally {
      if (generation === listGeneration) state.loading = false
    }
  }
  async function history(chat, older = false) {
    if (!chat?.saved || chat.loading) return
    chat.loading = true
    try {
      const floor = chat.messages[0]?.id
      let before = older ? chat.nextBefore : null
      if (older && !before) return
      let incoming = []
      do {
        const { data } = await api.history(chat.id, before ? { before } : {})
        incoming = data.results.concat(incoming)
        before = data.next_before
        // Refresh all loaded pages so old pending rows also receive final status.
        if (older || !floor || !before || before <= floor) break
      } while (before)
      chat.messages = mergeMessages(chat.messages, incoming)
      chat.nextBefore = before
      chat.loaded = true
      if (!chat.pending && !chat.retrying) chat.error = ''
    } catch (_) {
      chat.error = 'Unable to refresh messages. Your draft is saved here.'
    } finally {
      chat.loading = false
    }
  }
  async function send(chat) {
    if (!chat || chat.sending || chat.preferencesBusy || (!chat.pending && !chat.draft.trim())) return
    chat.pending = chat.pending || { request_id: requestId(), text: chat.draft.trim() }
    chat.sending = true
    chat.error = ''
    try {
      if (!chat.saved) {
        const { data } = await api.create({ id: chat.id })
        chat.saved = true
        chat.title = data.title
      }
      const { data } = await api.send(chat.id, chat.pending)
      chat.messages = mergeMessages(chat.messages, [data])
      chat.draft = ''
      chat.pending = null
      await list()
      await history(chat)
    } catch (error) {
      const rejected = error.response && error.response.status >= 400 && error.response.status < 500
      chat.error = rejected ? error.message : 'Delivery is unconfirmed. Retry send safely with the same request.'
      if (rejected) chat.pending = null
    } finally {
      chat.sending = false
    }
  }
  async function retry(chat, message) {
    if (chat.retrying) return
    const operation = message.operation
    const payload = chat.retryRequests[operation.id] || {
      message_id: message.id, request_id: requestId(), expected_version: operation.version
    }
    set(chat.retryRequests, operation.id, payload)
    chat.retrying = operation.id
    chat.error = ''
    try {
      const { data } = await api.retry(chat.id, payload)
      chat.messages = mergeMessages(chat.messages, [data])
      set(chat.retryRequests, operation.id, null)
    } catch (error) {
      chat.error = error.response ? error.message : 'Retry is unconfirmed. Try again safely.'
      if (error.response) set(chat.retryRequests, operation.id, null)
    } finally {
      chat.retrying = null
    }
  }
  return { state, start, list, history, send, retry, ...createChatPreferences(api, state, remember, list) }
}
